import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * StatusChangeAnimation Component
 * 
 * Provides visual feedback for status changes with smooth animations
 * and transitions. Supports different animation types for various
 * sync operations and status updates.
 * 
 * Requirements: 1.4, 2.5, 4.5 - Status change animations and visual feedback
 */
const StatusChangeAnimation = ({
  type = 'status_change',
  data = {},
  duration = 3000,
  onComplete = null,
  className = ''
}) => {
  const { theme } = useTheme();
  const [isVisible, setIsVisible] = useState(true);
  const [animationPhase, setAnimationPhase] = useState('enter');

  // Animation configuration based on type
  const getAnimationConfig = () => {
    switch (type) {
      case 'status_change':
        return {
          icon: getStatusIcon(data.newStatus),
          color: getStatusColor(data.newStatus),
          message: `Status changed to ${data.newStatus}`,
          animation: 'pulse-fade'
        };

      case 'sync_started':
        return {
          icon: '🔄',
          color: 'blue',
          message: `Starting sync: ${data.source_name}`,
          animation: 'spin-in'
        };

      case 'sync_completed':
        return {
          icon: data.success ? '✅' : '❌',
          color: data.success ? 'green' : 'red',
          message: data.success 
            ? `Sync completed: ${data.source_name}`
            : `Sync failed: ${data.source_name}`,
          animation: data.success ? 'bounce-in' : 'shake'
        };

      case 'file_uploaded':
        return {
          icon: '📁',
          color: 'blue',
          message: `File uploaded: ${data.fileName}`,
          animation: 'slide-up'
        };

      case 'assignment_changed':
        return {
          icon: '🔗',
          color: 'purple',
          message: `Assignment updated for ${data.parrotName}`,
          animation: 'fade-in'
        };

      default:
        return {
          icon: 'ℹ️',
          color: 'gray',
          message: 'Status updated',
          animation: 'fade-in'
        };
    }
  };

  // Get status icon based on status value
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'ready':
        return '✅';
      case 'processing':
      case 'queued':
        return '🔄';
      case 'failed':
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  // Get status color based on status value
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'ready':
        return 'green';
      case 'processing':
      case 'queued':
        return 'blue';
      case 'failed':
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Get color classes based on theme and color
  const getColorClasses = (color) => {
    const isDark = theme === 'dark';
    
    switch (color) {
      case 'green':
        return isDark 
          ? 'bg-green-900/20 border-green-700 text-green-300'
          : 'bg-green-50 border-green-200 text-green-800';
      case 'blue':
        return isDark
          ? 'bg-blue-900/20 border-blue-700 text-blue-300'
          : 'bg-blue-50 border-blue-200 text-blue-800';
      case 'red':
        return isDark
          ? 'bg-red-900/20 border-red-700 text-red-300'
          : 'bg-red-50 border-red-200 text-red-800';
      case 'purple':
        return isDark
          ? 'bg-purple-900/20 border-purple-700 text-purple-300'
          : 'bg-purple-50 border-purple-200 text-purple-800';
      default:
        return isDark
          ? 'bg-gray-800 border-gray-600 text-gray-300'
          : 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  // Get animation classes based on animation type and phase
  const getAnimationClasses = (animationType, phase) => {
    const baseClasses = 'transition-all duration-300 ease-in-out';
    
    switch (animationType) {
      case 'pulse-fade':
        return phase === 'enter'
          ? `${baseClasses} animate-pulse scale-105 opacity-100`
          : `${baseClasses} scale-95 opacity-0`;

      case 'spin-in':
        return phase === 'enter'
          ? `${baseClasses} animate-spin scale-100 opacity-100`
          : `${baseClasses} scale-0 opacity-0`;

      case 'bounce-in':
        return phase === 'enter'
          ? `${baseClasses} animate-bounce scale-100 opacity-100`
          : `${baseClasses} scale-95 opacity-0`;

      case 'shake':
        return phase === 'enter'
          ? `${baseClasses} animate-pulse scale-100 opacity-100`
          : `${baseClasses} scale-95 opacity-0`;

      case 'slide-up':
        return phase === 'enter'
          ? `${baseClasses} translate-y-0 opacity-100`
          : `${baseClasses} -translate-y-4 opacity-0`;

      case 'fade-in':
      default:
        return phase === 'enter'
          ? `${baseClasses} opacity-100`
          : `${baseClasses} opacity-0`;
    }
  };

  const config = getAnimationConfig();

  // Handle animation lifecycle
  useEffect(() => {
    // Start exit animation before duration ends
    const exitTimer = setTimeout(() => {
      setAnimationPhase('exit');
    }, duration - 300);

    // Hide component after exit animation
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
      if (onComplete) {
        onComplete();
      }
    }, duration);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(hideTimer);
    };
  }, [duration, onComplete]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`
        fixed top-4 right-4 z-50 max-w-sm
        ${getColorClasses(config.color)}
        ${getAnimationClasses(config.animation, animationPhase)}
        border rounded-lg shadow-lg p-3
        ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0 text-lg">
          {config.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {config.message}
          </p>
          
          {data.parrotName && (
            <p className="text-xs opacity-75 mt-1">
              Parrot: {data.parrotName}
            </p>
          )}
          
          {data.timestamp && (
            <p className="text-xs opacity-50 mt-1">
              {new Date(data.timestamp).toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* Progress indicator for processing states */}
        {(data.newStatus === 'processing' || data.newStatus === 'queued') && (
          <div className="flex-shrink-0">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin opacity-75"></div>
          </div>
        )}
      </div>

      {/* Progress bar for sync operations */}
      {type === 'sync_started' && (
        <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
          <div className="bg-current h-1 rounded-full animate-pulse" style={{ width: '30%' }}></div>
        </div>
      )}
    </div>
  );
};

export default StatusChangeAnimation;
